/**
 * Template Edit Modal
 * Edit all template information including Haravan integration and images
 */

import React, { useState, useEffect } from 'react';
import { X, Upload, ExternalLink } from 'lucide-react';
import { ProductTemplate } from '@/types';
import { imageUrlService } from '@/services/imageUrlService';

export interface EditTemplateData {
  name: string;
  category: string;
  material: string;
  display_order: number;
  haravan_product_id?: string | null;
  haravan_variant_id?: string | null;
  haravan_product_handle?: string | null;
  new_image?: File | null;
  clean_image?: File | null;
  mask_image?: File | null;
}

interface TemplateEditModalProps {
  isOpen: boolean;
  template: ProductTemplate | null;
  onClose: () => void;
  onSave: (data: EditTemplateData) => Promise<void>;
}

export const TemplateEditModal: React.FC<TemplateEditModalProps> = ({
  isOpen,
  template,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<EditTemplateData>({
    name: '',
    category: '',
    material: '',
    display_order: 1,
    haravan_product_id: null,
    haravan_variant_id: null,
    haravan_product_handle: null,
    new_image: null,
    clean_image: null,
    mask_image: null
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Previews
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [cleanPreview, setCleanPreview] = useState<string | null>(null);
  const [maskPreview, setMaskPreview] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        category: template.category,
        material: template.material,
        display_order: template.display_order,
        haravan_product_id: template.haravan_product_id || null,
        haravan_variant_id: template.haravan_variant_id || null,
        haravan_product_handle: template.haravan_product_handle || null,
        new_image: null,
        clean_image: null,
        mask_image: null
      });
      setImagePreview(null);
      setCleanPreview(null);
      setMaskPreview(null);
    }
    setError(null);
  }, [template, isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'new_image' | 'clean_image' | 'mask_image') => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Vui lòng chọn file hình ảnh');
        return;
      }

      setFormData(prev => ({ ...prev, [field]: file }));
      setError(null);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (field === 'new_image') setImagePreview(result);
        if (field === 'clean_image') setCleanPreview(result);
        if (field === 'mask_image') setMaskPreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        setError('Tên mẫu không được để trống');
        return;
      }
      if (!formData.category.trim()) {
        setError('Danh mục không được để trống');
        return;
      }
      if (!formData.material.trim()) {
        setError('Chất liệu không được để trống');
        return;
      }

      setIsSaving(true);
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi khi lưu');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white rounded-t-lg z-10">
          <h2 className="text-xl font-semibold text-gray-900">Chỉnh sửa thông tin mẫu</h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition disabled:opacity-50"
            title="Đóng"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[calc(100vh-16rem)] overflow-y-auto">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm border border-red-200">
              {error}
            </div>
          )}

          {/* Main Image */}
          <div className="border-b pb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Hình ảnh chính</h3>
            <div className="flex gap-4 mb-4">
              {template.image_path && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">Hiện tại</p>
                  <img
                    src={imageUrlService.getTemplateImageUrl(
                      template.image_path,
                      undefined,
                      template.updated_at || template.created_at
                    )}
                    alt="Full"
                    className="w-32 h-32 object-contain border rounded"
                  />
                </div>
              )}
              {imagePreview && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">Mới</p>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-32 h-32 object-contain border rounded"
                  />
                </div>
              )}
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Thay thế ảnh chính
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 transition">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, 'new_image')}
                className="hidden"
                id="replace-image"
                disabled={isSaving}
              />
              <label
                htmlFor="replace-image"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-6 h-6 text-gray-400 mb-1" />
                <span className="text-sm text-gray-600">
                  {formData.new_image ? formData.new_image.name : 'Chọn ảnh mới'}
                </span>
              </label>
            </div>
          </div>

          {/* Clean Image & Mask Image (For Cups) */}
          <div className="border-b pb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Clean Image */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Ảnh sạch (Clean)</h3>
              <div className="flex gap-4 mb-4">
                {template.clean_image_path && (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Hiện tại</p>
                    <img
                      src={imageUrlService.getTemplateImageUrl(template.clean_image_path)}
                      alt="Clean"
                      className="w-24 h-24 object-contain border rounded"
                    />
                  </div>
                )}
                {cleanPreview && (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Mới</p>
                    <img
                      src={cleanPreview}
                      alt="Preview"
                      className="w-24 h-24 object-contain border rounded"
                    />
                  </div>
                )}
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-indigo-400 transition">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 'clean_image')}
                  className="hidden"
                  id="replace-clean"
                  disabled={isSaving}
                />
                <label htmlFor="replace-clean" className="cursor-pointer flex flex-col items-center">
                  <Upload className="w-5 h-5 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-600">
                    {formData.clean_image ? formData.clean_image.name : 'Chọn ảnh sạch'}
                  </span>
                </label>
              </div>
            </div>

            {/* Mask Image */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Ảnh Mask (Red Box)</h3>
              <div className="flex gap-4 mb-4">
                {template.mask_image_path && (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Hiện tại</p>
                    <img
                      src={imageUrlService.getTemplateImageUrl(template.mask_image_path)}
                      alt="Mask"
                      className="w-24 h-24 object-contain border rounded"
                    />
                  </div>
                )}
                {maskPreview && (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Mới</p>
                    <img
                      src={maskPreview}
                      alt="Preview"
                      className="w-24 h-24 object-contain border rounded"
                    />
                  </div>
                )}
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-indigo-400 transition">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 'mask_image')}
                  className="hidden"
                  id="replace-mask"
                  disabled={isSaving}
                />
                <label htmlFor="replace-mask" className="cursor-pointer flex flex-col items-center">
                  <Upload className="w-5 h-5 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-600">
                    {formData.mask_image ? formData.mask_image.name : 'Chọn ảnh mask'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="border-t pt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Thông tin cơ bản</h3>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                  placeholder="Nhập tên mẫu"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Danh mục
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                  placeholder="e.g., iPhone 15/16, Latte Cup"
                />
              </div>

              {/* Material */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chất liệu
                </label>
                <input
                  type="text"
                  value={formData.material}
                  onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                  placeholder="e.g., TPU, Coffee Grounds"
                />
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Thứ tự hiển thị
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: Math.max(1, parseInt(e.target.value) || 1) })}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Haravan Integration */}
          <div className="border-t pt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Tích hợp Haravan
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </h3>
            <div className="space-y-4">
              {/* Haravan Product ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Haravan Product ID
                </label>
                <input
                  type="text"
                  value={formData.haravan_product_id || ''}
                  onChange={(e) => setFormData({ ...formData, haravan_product_id: e.target.value || null })}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                  placeholder="e.g., 1071839642"
                />
              </div>

              {/* Haravan Variant ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Haravan Variant ID
                </label>
                <input
                  type="text"
                  value={formData.haravan_variant_id || ''}
                  onChange={(e) => setFormData({ ...formData, haravan_variant_id: e.target.value || null })}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                  placeholder="e.g., 1234567890"
                />
              </div>

              {/* Haravan Product Handle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Haravan Product Handle
                </label>
                <input
                  type="text"
                  value={formData.haravan_product_handle || ''}
                  onChange={(e) => setFormData({ ...formData, haravan_product_handle: e.target.value || null })}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50"
                  placeholder="e.g., op-lung-iphone-17-pro-max"
                />
              </div>

              {/* Display Haravan sync data if available */}
              {template.haravan_sync_data && (
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p className="font-medium text-gray-700 mb-1">Dữ liệu đồng bộ từ Haravan:</p>
                  {template.haravan_sync_data.title && (
                    <p className="text-gray-600">Title: {template.haravan_sync_data.title}</p>
                  )}
                  {template.haravan_sync_data.price && (
                    <p className="text-gray-600">Price: {template.haravan_sync_data.price} {template.haravan_sync_data.currency || 'VND'}</p>
                  )}
                  {template.haravan_sync_data.inventory_quantity !== undefined && (
                    <p className="text-gray-600">Inventory: {template.haravan_sync_data.inventory_quantity}</p>
                  )}
                  {template.haravan_synced_at && (
                    <p className="text-gray-500 text-xs mt-1">
                      Synced at: {new Date(template.haravan_synced_at.seconds * 1000).toLocaleString('vi-VN')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-end gap-2 sticky bottom-0 bg-white rounded-b-lg">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium disabled:opacity-50"
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium disabled:opacity-50"
          >
            {isSaving ? 'Đang lưu...' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditModal;